import SEO from '@/components/ui-custom/SEO';

const Terms = () => {
  return (
    <>
      <SEO
        title="Terms of Service | Ard's Plumbing & Electric"
        description="Official Terms of Service for Ard's Plumbing & Electric. Read our comprehensive service agreements, warranty information, and operational policies."
        canonical="/terms-of-service"
        additionalSchema={{ '@context': 'https://schema.org', '@type': 'BreadcrumbList', 'itemListElement': [{ '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://ardsdothan.com/' }, { '@type': 'ListItem', 'position': 2, 'name': 'Terms of Service', 'item': 'https://ardsdothan.com/terms-of-service' }] }}
      />

      <section className="bg-brand-blue pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-heading font-black text-4xl lg:text-6xl text-white leading-tight mb-6">
            TERMS OF <span className="text-brand-yellow">SERVICE</span>
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Please review our comprehensive service agreement and policies.
          </p>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto prose prose-lg">
          
          <div className="mb-10 text-gray-700 pb-6 border-b border-gray-200">
            <p><strong>Effective Date:</strong> February 14, 2026</p>
            <p><strong>Last Updated:</strong> February 14, 2026</p>
            <p className="mt-4">
              This Terms of Service Agreement (hereinafter referred to as the “Terms” or “Agreement”) governs all plumbing, electrical, and related services provided by Ard’s Plumbing and Electric (hereinafter referred to as the “Company,” “we,” “our,” or “us”). By scheduling services, approving an estimate, signing a formal proposal, or otherwise authorizing work to be performed by the Company, the client (hereinafter referred to as the “Customer” or “you”) expressly agrees to be bound by these Terms.
            </p>
          </div>

          <div className="space-y-12">
            
            {/* 1. Estimates & Approval */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">1. Estimates and Approval</h2>
              <p className="text-gray-700">
                All estimates provided by the Company are valid for a period of thirty (30) days from the date of issuance, unless otherwise explicitly stated. The Company shall not commence work until one of the following conditions is met:
              </p>
              <ul className="list-disc pl-6 mt-4 mb-4 space-y-2 text-gray-700">
                <li>The estimate is approved by the Customer (digitally or in writing);</li>
                <li>Verbal authorization is provided by the Customer; or</li>
                <li>Work is initiated at the direct request of the Customer.</li>
              </ul>
              <p className="text-gray-700">
                Approval of an estimate constitutes a binding agreement to these Terms. Any additional work or modifications outside the scope of the original estimate require separate authorization from the Customer and will be subject to additional charges.
              </p>
            </div>

            {/* 2. Payment Terms */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">2. Payment Terms</h2>
              <p className="text-gray-700">Payment in full is due immediately upon the completion of services, unless an alternative arrangement has been expressly agreed upon in writing prior to the commencement of work. For large-scale projects, a deposit may be required before scheduling can be finalized.</p>
              <p className="text-gray-700 mt-4 mb-4">
                <strong>Accepted Payment Methods:</strong> We accept payment via cash, check, debit card, or credit card. Card payments are processed securely through our invoicing system powered by Jobber.
              </p>
              <p className="text-gray-700">Failure to remit payment in a timely manner may result in:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                <li>The assessment of late fees;</li>
                <li>Formal collection actions; and/or</li>
                <li>The filing of a mechanic’s lien against the property, where permitted by applicable law.</li>
              </ul>
            </div>

            {/* 3. Late Fees & Collections */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">3. Late Fees and Collections</h2>
              <p className="text-gray-700">Any balances remaining unpaid past the due date may accrue:</p>
              <ul className="list-disc pl-6 mt-2 mb-4 space-y-2 text-gray-700">
                <li>A late fee of 1.5% per month (18% annually); OR</li>
                <li>The maximum interest rate allowable by Alabama state law.</li>
              </ul>
              <p className="text-gray-700">
                In the event that the Company must pursue legal action or utilize a third-party collection agency to recover an unpaid balance, the Customer agrees to pay all associated collection costs, including, but not limited to, reasonable attorney’s fees and court costs.
              </p>
            </div>

            {/* 4. Access to Property */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">4. Access to Property</h2>
              <p className="text-gray-700">
                The Customer must provide safe, clear, and unobstructed access to all work areas. The Company shall not be held responsible for delays or additional costs resulting from:
              </p>
              <ul className="list-disc pl-6 mt-2 mb-4 space-y-2 text-gray-700">
                <li>Blocked or restricted access to the premises or equipment;</li>
                <li>Hazardous or unsanitary conditions;</li>
                <li>Unsecured pets or animals;</li>
                <li>Hidden structural damage; or</li>
                <li>Improper installations previously performed by third parties.</li>
              </ul>
              <p className="text-gray-700">
                If our technicians determine that unsafe conditions exist on the premises, the Company reserves the right to suspend or terminate work immediately without liability.
              </p>
            </div>

            {/* 5. Unforeseen Conditions */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">5. Unforeseen Conditions</h2>
              <p className="text-gray-700">
                Plumbing and electrical systems may contain hidden defects or complications that are not visible during an initial inspection. The Company assumes no liability for:
              </p>
              <ul className="list-disc pl-6 mt-2 mb-4 space-y-2 text-gray-700">
                <li>Pre-existing building code violations;</li>
                <li>Concealed leaks or water damage;</li>
                <li>Hidden wiring defects or electrical faults;</li>
                <li>Structural anomalies behind walls, floors, or ceilings; or</li>
                <li>The presence of mold, asbestos, or other hazardous materials.</li>
              </ul>
              <p className="text-gray-700">
                The discovery of any unforeseen issues may necessitate a change in the scope of work, resulting in additional repairs and corresponding charges.
              </p>
            </div>

            {/* 6. Permits & Code Compliance */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">6. Permits and Code Compliance</h2>
              <p className="text-gray-700">
                When legally required and agreed upon in the scope of work, the Company will obtain the necessary municipal permits. If the Customer explicitly declines the acquisition of a recommended or required permit, the Customer assumes full legal and financial liability for any code compliance failures, fines, or subsequent property issues.
              </p>
            </div>

            {/* 7. Change Orders */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">7. Change Orders</h2>
              <p className="text-gray-700">
                Any deviations, additions, or alterations to the original scope of work must be approved by the Customer. Verbal, digital, or written approval is legally binding. All additional work requested outside the initial estimate will be billed at the Company’s current standard rates.
              </p>
            </div>

            {/* 8. Cancellation Policy */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">8. Cancellation Policy</h2>
              <p className="text-gray-700">
                The Company requires adequate notice for the cancellation of any scheduled appointment. Cancellations made within twenty-four (24) hours of the scheduled service time may incur a cancellation fee. If Company technicians are dispatched to the property and are subsequently denied access or turned away, a standard trip charge will apply.
              </p>
            </div>

            {/* 9. Warranty */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">9. Limited Warranty</h2>
              <p className="text-gray-700">
                The Company warrants its labor and workmanship for a period of ninety (90) days from the date of completion, unless otherwise specified in writing. Materials, fixtures, and equipment supplied by the Company are subject strictly to the respective manufacturer’s warranties. This limited warranty is voided by and does not cover:
              </p>
              <ul className="list-disc pl-6 mt-2 mb-4 space-y-2 text-gray-700">
                <li>Customer misuse, abuse, or negligence;</li>
                <li>Failure to perform routine maintenance;</li>
                <li>Acts of God, natural disasters, or severe weather (including frozen pipes);</li>
                <li>Electrical power surges; or</li>
                <li>Tampering, alterations, or repairs performed by the Customer or third parties.</li>
              </ul>
              <p className="text-gray-700">
                All warranty claims must be submitted directly to the Company for evaluation prior to any corrective action being taken by outside parties.
              </p>
            </div>

            {/* 10. Limitation of Liability */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">10. Limitation of Liability</h2>
              <p className="text-gray-700">
                To the fullest extent permitted by applicable law, the Company’s total liability for any claim arising out of or relating to the services provided shall not exceed the total amount actually paid by the Customer for the specific service in dispute. Under no circumstances shall the Company be liable for indirect, incidental, punitive, or consequential damages, including but not limited to loss of use, business interruption, or property damage extending beyond incidents of our direct negligence.
              </p>
            </div>

            {/* 11. Customer Responsibility */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">11. Customer Responsibilities</h2>
              <p className="text-gray-700">To ensure safe and efficient service, the Customer is strictly responsible for:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                <li>Shutting off primary water or electrical power mains when instructed by technicians;</li>
                <li>Protecting, moving, or covering valuables, furniture, and fragile items near the work area;</li>
                <li>Informing the Company of any known hazards, structural weaknesses, or pre-existing conditions; and</li>
                <li>Ensuring that fundamental utilities (water, electricity) are active and accessible to facilitate repairs and testing.</li>
              </ul>
            </div>

            {/* 12. Chargebacks */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">12. Disputes and Chargebacks</h2>
              <p className="text-gray-700">
                The Customer agrees not to initiate credit card chargebacks for services that have been properly authorized and completed. Any disputes regarding billing or quality of service must be submitted to the Company in writing within seven (7) days of service completion. The Company reserves the right to pursue civil legal action against fraudulent or unwarranted chargebacks.
              </p>
            </div>

            {/* 13. Recording & Communication */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">13. Recording and Communication</h2>
              <p className="text-gray-700">
                For quality assurance and training purposes, telephone calls may be recorded and processed through our AI-assisted receptionist system. By initiating contact with the Company, the Customer provides express consent to the recording of communications and the receipt of automated operational messaging (e.g., appointment reminders and invoices).
              </p>
            </div>

            {/* 14. Indemnification */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">14. Indemnification</h2>
              <p className="text-gray-700">
                The Customer agrees to indemnify, defend, and hold harmless Ard’s Plumbing and Electric, its officers, employees, and agents, from and against any and all claims, damages, liabilities, or expenses arising from:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                <li>The Customer’s negligence or willful misconduct;</li>
                <li>The improper use or overloading of installed systems and fixtures;</li>
                <li>The Customer’s failure to adequately maintain equipment; or</li>
                <li>Unauthorized modifications or alterations made by third parties.</li>
              </ul>
            </div>

            {/* 15. Governing Law */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">15. Governing Law and Venue</h2>
              <p className="text-gray-700">
                This Agreement shall be governed by and construed in accordance with the laws of the State of Alabama, without regard to its conflict of law principles. Any legal suit, action, or proceeding arising out of or relating to these Terms shall be instituted exclusively in the state or federal courts located in Houston County, Alabama.
              </p>
            </div>

            {/* 16. Severability */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">16. Severability</h2>
              <p className="text-gray-700">
                If any provision of these Terms is determined to be invalid, illegal, or unenforceable by a court of competent jurisdiction, such invalidity shall not affect the enforceability of any other provision of this Agreement, and the remaining provisions shall remain in full force and effect.
              </p>
            </div>

            {/* 17. Entire Agreement */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">17. Entire Agreement</h2>
              <p className="text-gray-700">
                These Terms constitute the sole and entire agreement between the Company and the Customer regarding the subject matter herein, superseding all prior and contemporaneous understandings, agreements, representations, and warranties, both written and oral. These Terms may only be amended or modified by a written document signed by an authorized representative of the Company.
              </p>
            </div>

            {/* Footer / Contact Info */}
            <div className="pt-10 border-t border-gray-200 text-gray-700 bg-gray-50 p-6 rounded-xl mt-12">
              <h3 className="font-bold text-lg mb-2 text-brand-navy">Ard’s Plumbing and Electric</h3>
              <p className="mb-1">733 West State Hwy 92</p>
              <p className="mb-1">Newton, Alabama 36352</p>
              <p className="mb-1">Phone: <a href="tel:3346925537" className="text-brand-coral hover:underline">(334) 692-5537</a></p>
              <p>Email: <a href="mailto:support@ardsdothan.com" className="text-brand-coral hover:underline">Robert.ard@ardsdothan.com</a></p>
            </div>
            
          </div>
        </div>
      </section>
    </>
  );
};

export default Terms;